import { theme, Flex, Typography } from "antd";
import type { GlobalToken } from "antd";
import styled from "styled-components";
import { useTranslate } from "@refinedev/core";
import { FontPreviewText } from "pages/media/brandkit/components/FontPreview";
import type { BrandKitOption } from "./types";
import { radius } from "@libertify/ui";

const { Text } = Typography;

// Fixed width of the name column, so the colors/font block after it lines up
// across rows instead of trailing right after each (differently long) name.
const NAME_COLUMN_WIDTH = 190;

/**
 * One row of the video config's brand kit Select — used for both the option
 * list (via `optionRender`) and the closed control (via `labelRender`), so
 * the selected value looks the same as its entry in the dropdown.
 * Shows: logo (if any) · name · "(default)" when it's the org's preference ·
 * primary/secondary color swatches · the title font previewed on sample text.
 */
export const BrandKitOptionRow = ({ option }: { option: BrandKitOption }) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const colors = [
    option.primaryColor,
    option.secondaryColor,
    option.titleColor,
  ].filter((color): color is string => !!color);

  return (
    <Flex align="center" gap={10} style={{ width: "100%", minWidth: 0 }}>
      {option.logoUrl && (
        <LogoImg src={option.logoUrl} alt={option.label} $token={token} />
      )}
      {/* Fixed width so the colors/font block below always starts at the
          same x position, regardless of how long each brand kit's name is. */}
      <Flex
        align="center"
        gap={4}
        style={{ width: NAME_COLUMN_WIDTH, minWidth: 0, flexShrink: 0 }}
      >
        <Text ellipsis style={{ fontWeight: 600 }}>
          {option.label}
        </Text>
        {option.isOrgDefault && (
          <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
            ({t("createExperience.videoConfig.brandKit.defaultTag")})
          </Text>
        )}
      </Flex>
      <Flex align="center" gap={14} style={{ flexShrink: 0 }}>
        {colors.length > 0 && (
          <Flex align="center">
            {colors.map((color, index) => (
              <ColorDot
                key={`${color}-${index}`}
                $color={color}
                $overlap={index > 0}
                $token={token}
              />
            ))}
          </Flex>
        )}
        <FontPreviewText
          font={option.titleFontUrl ?? ""}
          text={t("createExperience.videoConfig.brandKit.headingSample")}
          style={{
            fontSize: token.fontSizeSM,
            textAlign: "left",
            padding: 0,
            whiteSpace: "nowrap",
            color: token.colorText,
          }}
        />
      </Flex>
    </Flex>
  );
};

export default BrandKitOptionRow;

// ─── Styled components ───

const LogoImg = styled.img<{ $token: GlobalToken }>`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  object-fit: contain;
  border: 1px solid ${({ $token }) => $token.colorBorderSecondary};
  flex-shrink: 0;
`;

const ColorDot = styled.span<{
  $color: string;
  $overlap: boolean;
  $token: GlobalToken;
}>`
  width: 18px;
  height: 18px;
  border-radius: ${radius.circle};
  background: ${({ $color }) => $color};
  border: 2px solid ${({ $token }) => $token.colorBgContainer};
  box-shadow: 0 0 0 1px ${({ $token }) => $token.colorBorderSecondary};
  margin-left: ${({ $overlap }) => ($overlap ? "-7px" : "0")};
  flex-shrink: 0;
`;
